import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import {
  DEFAULT_PUBLIC_FINISH_KEYS,
  FinishTypeOption,
  formatFinishTypeKeyLabel,
  getFinishTypeOption,
  mapFinishKeysToOptions,
} from '../lib/finishTypes';

interface FinishTypeRow {
  key: string;
  label?: string | null;
  description?: string | null;
  sort_order: number | null;
  default_colourway_id?: string | null;
}

export function usePublicFinishTypes() {
  const [rows, setRows] = useState<FinishTypeRow[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const loadFinishTypes = async () => {
      try {
        setLoading(true);
        const baseQuery = (select: string) =>
          supabase
            .from('finish_types')
            .select(select)
            .eq('is_public_visible', true)
            .order('sort_order', { ascending: true })
            .order('key', { ascending: true });

        let data: FinishTypeRow[] | null = null;
        let error: { message?: string } | null = null;

        const fullResult = await baseQuery(
          'key, label, description, sort_order, default_colourway_id'
        );
        data = (fullResult.data ?? null) as FinishTypeRow[] | null;
        error = fullResult.error;

        if (error) {
          const legacyResult = await baseQuery('key, sort_order, label, description');
          data = (legacyResult.data ?? null) as FinishTypeRow[] | null;
          error = legacyResult.error;
        }

        if (error) {
          const minimalResult = await baseQuery('key, sort_order');
          data = (minimalResult.data ?? null) as FinishTypeRow[] | null;
          error = minimalResult.error;
        }

        if (error) throw error;
        if (!mounted) return;
        setRows(data ?? []);
      } catch (error) {
        console.warn('[usePublicFinishTypes] Falling back to default finish list', error);
        if (!mounted) return;
        setRows(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadFinishTypes();

    return () => {
      mounted = false;
    };
  }, []);

  const finishOptions: FinishTypeOption[] = useMemo(() => {
    if (!rows || rows.length === 0) {
      return mapFinishKeysToOptions(DEFAULT_PUBLIC_FINISH_KEYS);
    }

    const mapped = rows
      .map((row, index) => {
        const fallback = getFinishTypeOption(row.key);
        const sortOrder = typeof row.sort_order === 'number'
          ? row.sort_order
          : fallback?.sortOrder ?? ((index + 1) * 10);

        const label = row.label?.trim() || fallback?.label || formatFinishTypeKeyLabel(row.key);
        const description = row.description?.trim() || fallback?.description || 'Custom finish type.';

        return {
          value: row.key,
          label,
          description,
          sortOrder,
          defaultColourwayId: row.default_colourway_id ?? null,
        } as FinishTypeOption;
      })
      .sort((a, b) => (a.sortOrder - b.sortOrder) || a.label.localeCompare(b.label));

    return mapped.length > 0 ? mapped : mapFinishKeysToOptions(DEFAULT_PUBLIC_FINISH_KEYS);
  }, [rows]);

  const allowedFinishKeys = useMemo(() => finishOptions.map(option => option.value), [finishOptions]);

  return {
    finishOptions,
    allowedFinishKeys,
    loading,
  };
}
